import axios from 'axios';
import { API_BASE_URL } from '../config/constants';

// Define login history type
export interface LoginHistory {
  _id: string;
  userId: string;
  device: string;
  browser: string;
  ip: string;
  status: 'success' | 'failed';
  failReason?: string | null;
  createdAt: string;
  __v: number;
}

// Define audit log type
export interface AuditLog {
  _id: string;
  entityType: string;
  entityId: string;
  action: 'create' | 'update' | 'delete' | 'view' | 'login' | 'logout' | 'restore' | 'api_request';
  description?: string;
  timestamp: string;
  performedBy?: {
    _id: string;
    fullName: string;
  };
  userId?: {
    _id: string;
    name: string;
    email: string;
    fullName?: string;
  };
  metadata?: {
    requestId: string;
    ip: string;
    userAgent: string;
    timestamp: string;
    route: string;
    method: string;
    statusCode?: number;
    duration?: number;
    queryParams?: any;
  };
  previousState?: Record<string, any>;
  newState?: Record<string, any>;
  changes?: Array<{
    field: string;
    oldValue: any;
    newValue: any;
  }> | Record<string, any>;
  document?: Record<string, any>;
  operationHash?: string;
}

// Define pagination response type
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

// Filter interface for getLogs
export interface AuditLogFilters {
  userId?: string;
  entityType?: string;
  entityId?: string;
  action?: string;
  startDate?: string;
  endDate?: string;
  ip?: string;
}

// Stats interface
export interface AuditLogStats {
  totalToday: number;
  totalThisWeek: number;
  totalThisMonth: number;
  byAction: { action: string; count: number }[];
  byEntityType: { entityType: string; count: number }[];
  topUsers: { userId: string; fullName: string; count: number }[];
  topIPs: { ip: string; count: number }[];
  hourlyDistribution: { hour: number; count: number }[];
}

function getHeaders() {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
}

// Audit log service
export const auditLogService = {
  /**
   * Get all audit logs with optional filters
   */
  getLogs: async (
    filters: AuditLogFilters = {},
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedResponse<AuditLog>> => {
    try {
      const params: any = { page, limit };

      if (filters.userId) params.userId = filters.userId;
      if (filters.entityType) params.entityType = filters.entityType;
      if (filters.entityId) params.entityId = filters.entityId;
      if (filters.action) params.action = filters.action;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.ip) params.ip = filters.ip;

      const response = await axios.get(`${API_BASE_URL}/admin/audit-logs`, {
        params,
        headers: getHeaders()
      });

      if (response.data && response.data.status === 'success') {
        return {
          data: Array.isArray(response.data.data) ? response.data.data : [],
          pagination: response.data.pagination || { total: 0, page, pages: 0, limit }
        };
      }

      return { data: [], pagination: { total: 0, page, pages: 0, limit } };
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return { data: [], pagination: { total: 0, page, pages: 0, limit } };
    }
  },

  /**
   * Get audit log statistics
   */
  getStats: async (filters?: { startDate?: string; endDate?: string }): Promise<AuditLogStats> => {
    try {
      const params: any = {};
      if (filters?.startDate) params.startDate = filters.startDate;
      if (filters?.endDate) params.endDate = filters.endDate;

      const response = await axios.get(`${API_BASE_URL}/admin/audit-logs/stats`, {
        params,
        headers: getHeaders()
      });

      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error fetching audit log stats:', error);
      return {
        totalToday: 0,
        totalThisWeek: 0,
        totalThisMonth: 0,
        byAction: [],
        byEntityType: [],
        topUsers: [],
        topIPs: [],
        hourlyDistribution: []
      };
    }
  },

  /**
   * Export audit logs as CSV file download
   */
  exportLogs: async (filters: AuditLogFilters = {}, format: 'csv' | 'json' = 'csv'): Promise<void> => {
    try {
      const params: any = { format };
      if (filters.userId) params.userId = filters.userId;
      if (filters.entityType) params.entityType = filters.entityType;
      if (filters.entityId) params.entityId = filters.entityId;
      if (filters.action) params.action = filters.action;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.ip) params.ip = filters.ip;

      const response = await axios.get(`${API_BASE_URL}/admin/audit-logs/export`, {
        params,
        headers: getHeaders(),
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv;charset=utf-8;' : 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      throw error;
    }
  },

  /**
   * Get history of changes for an entity
   */
  getEntityHistory: async (
    entityType: string,
    entityId: string,
    page: number = 1,
    limit: number = 10
  ): Promise<PaginatedResponse<AuditLog>> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/audit-logs/entity/${entityType}/${entityId}`,
        {
          params: { page, limit },
          headers: getHeaders()
        }
      );

      if (response.data && response.data.status === 'success') {
        return {
          data: Array.isArray(response.data.data) ? response.data.data : [],
          pagination: response.data.pagination || { total: 0, page, pages: 0, limit }
        };
      }

      return { data: [], pagination: { total: 0, page, pages: 0, limit } };
    } catch (error) {
      console.error('Error fetching entity history:', error);
      return { data: [], pagination: { total: 0, page, pages: 0, limit } };
    }
  },

  /**
   * Get login history for a specific user
   */
  getUserLoginHistory: async (
    userId: string,
    page = 1,
    limit = 10
  ): Promise<PaginatedResponse<LoginHistory>> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/audit-logs/user/${userId}/logins`,
        {
          params: { page, limit },
          headers: getHeaders()
        }
      );

      if (response.data && response.data.status === 'success') {
        return {
          data: response.data.data || [],
          pagination: response.data.pagination || { total: 0, page, pages: 0, limit }
        };
      }

      return { data: [], pagination: { total: 0, page, pages: 0, limit } };
    } catch (error) {
      console.error('Error fetching user login history:', error);
      throw error;
    }
  },

  /**
   * Get login history for the current user
   */
  getMyLoginHistory: async (
    page = 1,
    limit = 10
  ): Promise<PaginatedResponse<LoginHistory>> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/audit-logs/my-logins`, {
        params: { page, limit },
        headers: getHeaders()
      });

      if (response.data && response.data.status === 'success') {
        return {
          data: response.data.data || [],
          pagination: response.data.pagination || { total: 0, page, pages: 0, limit }
        };
      }

      return { data: [], pagination: { total: 0, page, pages: 0, limit } };
    } catch (error) {
      console.error('Error fetching my login history:', error);
      throw error;
    }
  },

  /**
   * Format date to a user-friendly string
   */
  formatDate: (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('fa-IR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch (e) {
      return dateString || '';
    }
  },

  /**
   * Get user-friendly display name for action types
   */
  getActionDisplayName: (action: string): string => {
    const actionMap: Record<string, string> = {
      'create': 'ایجاد',
      'update': 'ویرایش',
      'delete': 'حذف',
      'view': 'مشاهده',
      'login': 'ورود',
      'logout': 'خروج',
      'restore': 'بازیابی',
      'api_request': 'درخواست HTTP'
    };
    return actionMap[action] || action;
  },

  /**
   * Get user-friendly display name for entity types
   */
  getEntityTypeDisplayName: (entityType: string): string => {
    const entityMap: Record<string, string> = {
      'User': 'کاربر',
      'Product': 'محصول',
      'Store': 'فروشگاه',
      'Order': 'سفارش',
      'Ticket': 'تیکت',
      'TicketMessage': 'پیام تیکت',
      'Chat': 'چت',
      'Message': 'پیام',
      'BankAccount': 'حساب بانکی',
      'SellerApplication': 'درخواست فروشنده',
      'KycVerification': 'احراز هویت',
      'BannerSlot': 'جایگاه بنر',
      'BannerReservation': 'رزرو بنر',
      'BannerAnalytics': 'آمار بنر',
      'DiscountCode': 'کد تخفیف',
      'ProductReview': 'نظر محصول',
      'ProductEditRequest': 'درخواست ویرایش محصول',
      'StoreEditRequest': 'درخواست ویرایش فروشگاه',
      'AccountType': 'نوع حساب',
      'ActivityArea': 'حوزه فعالیت',
      'Role': 'نقش',
      'Setting': 'تنظیمات',
      'Company': 'شرکت',
      'AppRelease': 'نسخه اپلیکیشن',
      'Story': 'استوری',
      'StoreEmployee': 'کارمند فروشگاه',
      'StoreRole': 'نقش فروشگاه',
      'Tenant': 'تنانت',
      'HttpRequest': 'درخواست HTTP'
    };
    return entityMap[entityType] || entityType;
  },

  /**
   * Get user-friendly display name for field names
   */
  getFieldDisplayName: (field: string): string => {
    const fieldMap: Record<string, string> = {
      'name': 'نام',
      'email': 'ایمیل',
      'phone': 'تلفن',
      'phoneNumber': 'شماره تلفن',
      'status': 'وضعیت',
      'description': 'توضیحات',
      'title': 'عنوان',
      'price': 'قیمت',
      'costPrice': 'قیمت خرید',
      'address': 'آدرس',
      'isActive': 'فعال',
      'role': 'نقش',
      'permissions': 'دسترسی‌ها',
      'isAdmin': 'مدیر',
      'isSuperAdmin': 'مدیر ارشد',
      'isBanned': 'مسدود',
      'avatar': 'آواتار',
      'password': 'رمز عبور',
      'quantity': 'تعداد',
      'stock': 'موجودی',
      'category': 'دسته‌بندی',
      'isVerified': 'تایید شده',
      'lastLogin': 'آخرین ورود'
    };
    return fieldMap[field] || field;
  },

  /**
   * Restore an entity to a previous state based on a specific audit log
   */
  restoreEntityToState: async (
    entityType: string,
    entityId: string,
    auditLogId: string
  ): Promise<void> => {
    try {
      await axios.post(
        `${API_BASE_URL}/admin/audit-logs/entity/${entityType}/${entityId}/restore`,
        { auditLogId },
        { headers: getHeaders() }
      );
    } catch (error) {
      console.error('Error restoring entity to previous state:', error);
      throw error;
    }
  }
};

export default auditLogService;
